import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaChartLine,
  FaCode,
  FaPlug,
  FaRobot,
  FaShieldAlt,
  FaShoppingCart,
  FaWordpress,
} from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import {
  COMPLETE_SERVICE_DIRECTORY,
  DELIVERY_FLOW,
  HERO_SYSTEM_NODES,
  PROOF_METRICS,
  RELIABILITY_PROOF,
  SERVICE_PILLARS,
  TECHNOLOGY_KEYWORDS,
} from '../data/servicesPremium';
import '../styles/services-premium.css';

const PILLAR_ICONS = {
  code: FaCode,
  robot: FaRobot,
  cart: FaShoppingCart,
  wordpress: FaWordpress,
  plug: FaPlug,
  shield: FaShieldAlt,
};

function PillarIcon({ iconKey }: { iconKey: string }) {
  const Icon = PILLAR_ICONS[iconKey] || FaChartLine;
  return <Icon aria-hidden="true" />;
}

const Services = () => {
  return (
    <>
      <PageSeo path="/services" />

      <div className="services-premium">
        <div className="header-spacer" />

        {/* Hero */}
        <section className="services-premium-hero" aria-labelledby="services-premium-heading">
          <div className="services-premium__shell">
            <div className="services-premium-hero__layout">
              <div>
                <p className="services-premium-hero__kicker">Production-focused engineering agency</p>
                <h1 id="services-premium-heading" className="services-premium-hero__title">
                  Expert web development, AI automation, ecommerce, and integration services
                </h1>
                <p className="services-premium-hero__lead">
                  We design, engineer, integrate, optimize, and support production-ready digital systems
                  for teams that need dependable delivery—React and Next.js front ends, Python and Node.js
                  backends, Magento and WooCommerce, WordPress Sage, AI workflows, CRM/ERP connections,
                  and measurable post-launch improvement.
                </p>
                <div className="services-premium-hero__actions">
                  <Link to="/getintouch" className="services-premium-btn services-premium-btn--primary">
                    Book a technical audit
                  </Link>
                  <a href="#services-directory" className="services-premium-btn services-premium-btn--ghost">
                    View service directory
                  </a>
                </div>
              </div>

              <div className="services-premium-map" aria-hidden="true">
                {HERO_SYSTEM_NODES.map((node, index) => (
                  <div key={node} className="services-premium-map__node">
                    {node}
                    {index < HERO_SYSTEM_NODES.length - 1 ? (
                      <span className="services-premium-map__connector" />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Proof metrics */}
        <section className="services-premium-proof" aria-labelledby="services-proof-heading">
          <div className="services-premium__shell">
            <h2 id="services-proof-heading" className="sr-only">
              Proof of delivery capability
            </h2>
            <div className="services-premium-proof__grid">
              {PROOF_METRICS.map((item) => (
                <article key={item.id} className="services-premium-proof__card">
                  <span className="services-premium-proof__value">{item.value}</span>
                  <span className="services-premium-proof__label">{item.label}</span>
                  <p className="services-premium-proof__detail">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Delivery flow */}
        <section className="services-premium-section" aria-labelledby="services-flow-heading">
          <div className="services-premium__shell">
            <div className="services-premium-flow-intro">
              <div className="services-premium-section__header services-premium-flow-intro__copy">
                <p className="services-premium-section__kicker">How we deliver</p>
                <h2 id="services-flow-heading" className="services-premium-section__title">
                  A directional delivery flow from discovery to long-term support
                </h2>
                <p className="services-premium-section__intro">
                  Every engagement follows a clear path—audit the problem, architect the solution, build
                  with senior engineers, integrate your stack, optimize for speed and conversion, and
                  support production systems after launch.
                </p>
              </div>
              <figure className="services-premium-flow-intro__visual">
                <img
                  src="/img/about-extra-2.svg"
                  alt=""
                  width={560}
                  height={426}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            </div>
            <div className="services-premium-flow">
              {DELIVERY_FLOW.map((step) => (
                <article key={step.id} className="services-premium-flow__step">
                  <span className="services-premium-flow__num">{step.step}</span>
                  <h3 className="services-premium-flow__name">{step.title}</h3>
                  <p className="services-premium-flow__desc">{step.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Service pillars */}
        <section className="services-premium-section services-premium-section--dark" aria-labelledby="services-pillars-heading">
          <div className="services-premium__shell">
            <div className="services-premium-section__header">
              <p className="services-premium-section__kicker">Core capabilities</p>
              <h2 id="services-pillars-heading" className="services-premium-section__title">
                Six service pillars built for expert, integration-ready delivery
              </h2>
              <p className="services-premium-section__intro">
                Highly skilled engineering across web products, AI automation, ecommerce, CMS, integrations,
                and performance—backed by committed wording, clear scope, and production proof.
              </p>
            </div>
            <div className="services-premium-pillars">
              {SERVICE_PILLARS.map((pillar) => (
                <article key={pillar.id} className="services-premium-pillar">
                  <span className="services-premium-pillar__icon">
                    <PillarIcon iconKey={pillar.iconKey} />
                  </span>
                  <h3 className="services-premium-pillar__title">{pillar.title}</h3>
                  <p className="services-premium-pillar__proof">{pillar.proof}</p>
                  <ul className="services-premium-pillar__list">
                    {pillar.services.map((service) => (
                      <li key={service}>{service}</li>
                    ))}
                  </ul>
                  <div className="services-premium-pillar__links">
                    {pillar.links.map((link) => (
                      <Link key={link.to} to={link.to} className="services-premium-pillar__link">
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Complete directory */}
        <section
          id="services-directory"
          className="services-premium-section"
          aria-labelledby="services-directory-heading"
        >
          <div className="services-premium__shell">
            <div className="services-premium-section__header">
              <p className="services-premium-section__kicker">Complete catalog</p>
              <h2 id="services-directory-heading" className="services-premium-section__title">
                All services we cover—nothing missed
              </h2>
              <p className="services-premium-section__intro">
                Every current service route and keyword capability in one directory. Click through to
                dedicated landing pages or start with a consultation for custom scope.
              </p>
            </div>
            <div className="services-premium-directory">
              {COMPLETE_SERVICE_DIRECTORY.map((group) => (
                <article key={group.id} className="services-premium-directory__group">
                  <h3 className="services-premium-directory__title">{group.title}</h3>
                  <ul className="services-premium-directory__list">
                    {group.items.map((item) => (
                      <li key={`${group.id}-${item.label}`} className="services-premium-directory__item">
                        <Link to={item.to}>{item.label}</Link>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Reliability proof */}
        <section className="services-premium-section" aria-labelledby="services-reliability-heading">
          <div className="services-premium__shell">
            <div className="services-premium-section__header">
              <p className="services-premium-section__kicker">Why teams trust us</p>
              <h2 id="services-reliability-heading" className="services-premium-section__title">
                Reliability, expertise, and committed delivery you can verify
              </h2>
              <p className="services-premium-section__intro">
                We show proof through architecture clarity, integration QA, maintainable code, and
                post-launch support—not vague promises.
              </p>
            </div>
            <div className="services-premium-reliability">
              {RELIABILITY_PROOF.map((item) => (
                <article key={item.id} className="services-premium-reliability__card">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Technology keywords */}
        <section className="services-premium-tech" aria-labelledby="services-tech-heading">
          <div className="services-premium__shell">
            <div className="services-premium-section__header">
              <p className="services-premium-section__kicker">
                Technology & keywords
              </p>
              <h2 id="services-tech-heading" className="services-premium-section__title">
                Stacks and integrations we engineer every day
              </h2>
            </div>
            <div className="services-premium-tech__chips">
              {TECHNOLOGY_KEYWORDS.map((keyword) => (
                <span key={keyword} className="services-premium-tech__chip">
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="services-premium-cta" aria-labelledby="services-cta-heading">
          <div className="services-premium__shell">
            <div className="services-premium-cta__panel">
              <div className="services-premium-cta__copy">
                <h2 id="services-cta-heading">
                  Not sure which service fits your project?
                </h2>
                <p>
                  We can audit your website, workflow, ecommerce stack, or automation idea and recommend
                  the fastest reliable path—with clear scope, senior engineering, and measurable outcomes.
                </p>
              </div>
              <div className="services-premium-cta__actions">
                <Link to="/getintouch" className="services-premium-btn services-premium-btn--primary">
                  Request a technical audit <FaArrowRight aria-hidden="true" />
                </Link>
                <Link to="/portfolio" className="services-premium-btn services-premium-btn--secondary">
                  See portfolio proof
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Newsletters />
      </div>
    </>
  );
};

export default Services;
